import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ProfileField from './profile/ProfileField';
import FormInput from '../../../../commonComponents/forms/FormInput';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import { PROFILE_BLOCK } from '../../../../services/utilityFunctions';
import { getValidationRules } from '../../helpers/helper';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';

function stateFromProps(member = {}) {
  const softSkills = member.softSkills || {};
  let state = {
    experienceTime: softSkills.experienceTime || '',
    competenciesDomains: softSkills.competenciesDomains || [],
    biography: softSkills.biography || '',
  };
  if (member.isTrainer) {
    state = {
      ...state,
      trainer: member.trainer
        ? {
            trainerType: member?.trainer?.trainerType || 'internal',
            professionalPath: member?.trainer?.professionalPath || '',
            biography: member?.trainer?.biography || '',
          }
        : {
            trainerType: 'internal',
            professionalPath: '',
            biography: '',
          },
    };
  }
  return state;
}

/**
 * Form to update the current logged user
 */
function ExperiencesForm(props) {
  const {
    t,
    member,
    onMemberChange,
    organizationCompetenciesDomains,
    onSkipStep,
  } = props;
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);

  const formId = 'experienceForm';
  const validationRules = useMemo(
    () =>
      getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.EXPERIENCE, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );

  const handleSubmit = async () => {
    const params = {
      experienceTime: state.experienceTime,
      competenciesDomains: state.competenciesDomains,
      biography: state.biography,
    };
    if (member.isTrainer) {
      onMemberChange(
        params,
        'softSkills',
        {
          ...state.trainer,
          biography: state.biography,
        },
        'trainer',
      );
    } else {
      onMemberChange(params, 'softSkills');
    }
  };

  const handleRemoveOption = useCallback(
    field => value =>
      setState(prevState => ({
        ...prevState,
        [field]: prevState[field].filter(v => v !== value),
      })),
    [],
  );

  const handleChange = useCallback((field, value) => {
    // fields for trainer
    if (field.startsWith('trainer.')) {
      const fieldToUpdate = field.split('.');
      setState(prevState => ({
        ...prevState,
        trainer: {
          ...prevState.trainer,
          [fieldToUpdate[1]]: value,
        },
      }));
    } else if (field === 'competenciesDomains') {
      setState(prevState => ({
        ...prevState,
        [field]: prevState[field].concat(value),
      }));
    } else {
      setState(prevState => ({ ...prevState, [field]: value }));
    }
  }, []);

  const inputProps = useCallback(
    field => {
      if (field !== 'competenciesDomains') {
        return undefined;
      }
      return {
        items:
          organizationCompetenciesDomains?.map(cd => ({
            id: cd._id,
            text: cd.name,
          })) || [],
        multiple: true,
        onUnselect: handleRemoveOption('competenciesDomains'),
      };
    },
    [organizationCompetenciesDomains, handleRemoveOption],
  );
  const handleTypeChange = e => {
    const newType = e.target.value;
    setState(prevState => ({
      ...prevState,
      trainer: {
        ...prevState.trainer,
        trainerType: newType,
      },
    }));
  };
  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_experience')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ProfileField
              field="experienceTime"
              type="number"
              value={state.experienceTime}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.EXPERIENCE}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="competenciesDomains"
              type="select"
              value={state.competenciesDomains}
              onChange={handleChange}
              inputProps={inputProps}
              disabled
            />
            <ProfileField
              field="biography"
              type="textarea"
              value={state.biography}
              onChange={handleChange}
              inputProps={inputProps}
              containerClassName="biographyClass col-lg-7 col-sm-12"
              block={PROFILE_BLOCK.EXPERIENCE}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
          </div>
          {member.isTrainer && (
            <div className="trainer-tab experience-trainer-section">
              <hr />
              <FormSubtitle title={t('trainers.edit.experience')} />
              <div className="form-supplier_holder">
                <div className="form-group m-form__group row">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
                  <label className="col-form-label col-2">
                    {t('trainers.edit.type')}
                  </label>
                  <div className="form-trainer_holder col-7">
                    <div className="row">
                      <div className="col-4">
                        <input
                          type="radio"
                          id="internal"
                          name="type"
                          required
                          value="internal"
                          checked={state.trainer.trainerType === 'internal'}
                          onChange={handleTypeChange}
                        />
                        <label
                          className="col-12 radio-experience"
                          htmlFor="internal"
                        >
                          <svg
                            className="svg-internal_link"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {/* eslint-disable-next-line max-len */}
                            <path
                              d="M14.1592 7.28604H13.0479C12.8177 7.28604 12.6311 7.47633 12.6311 7.71105V12.8784C12.6311 13.1527 12.4122 13.3758 12.1433 13.3758H2.43247C2.16358 13.3758 1.94487 13.1527 1.94487 12.8784V2.97541C1.94487 2.70108 2.16358 2.47786 2.43247 2.47786H7.7589C7.98906 2.47786 8.17566 2.28757 8.17566 2.05285V0.919487C8.17566 0.684768 7.98906 0.494476 7.7589 0.494476H2.43247C1.09118 0.494476 0 1.60744 0 2.97541V12.8784C0 14.2464 1.09124 15.3593 2.43247 15.3593H12.1432C13.4846 15.3593 14.5759 14.2464 14.5759 12.8784V7.71111C14.576 7.47633 14.3894 7.28604 14.1592 7.28604Z"
                              fill="white"
                            />
                            {/* eslint-disable-next-line max-len */}
                            <path
                              d="M6.87949 9.33264H11.3001C11.5302 9.33264 11.7168 9.14235 11.7168 8.90763V7.77427C11.7168 7.53955 11.5302 7.34926 11.3001 7.34926H9.78284L15.4921 1.527C15.6549 1.36102 15.6549 1.09196 15.4921 0.925923L14.7063 0.124466C14.6281 0.044734 14.5221 -3.43323e-05 14.4116 -3.43323e-05C14.301 -3.43323e-05 14.195 0.044734 14.1169 0.124466L8.4076 5.94683V4.39962C8.4076 4.1649 8.221 3.97461 7.99084 3.97461H6.87949C6.64933 3.97461 6.46273 4.1649 6.46273 4.39962V8.90763C6.46273 9.14235 6.64933 9.33264 6.87949 9.33264Z"
                              fill="white"
                            />
                          </svg>
                          {t('trainers.edit.internal')}
                        </label>
                      </div>
                      <div className="col-4">
                        <input
                          type="radio"
                          id="external"
                          name="type"
                          value="external"
                          checked={state.trainer.trainerType === 'external'}
                          onChange={handleTypeChange}
                        />
                        <label
                          className="col-12 radio-experience"
                          htmlFor="external"
                        >
                          <i className="fas fa-external-link-alt" />
                          {t('trainers.edit.external')}
                        </label>
                      </div>
                      <div className="col-4">
                        <input
                          type="radio"
                          id="remote"
                          name="type"
                          value="remote"
                          checked={state.trainer.trainerType === 'remote'}
                          onChange={handleTypeChange}
                        />
                        <label
                          className="col-12 radio-experience"
                          htmlFor="remote"
                        >
                          <i className="fa fa-headphones" />
                          {t('trainers.edit.remote')}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <FormInput
                  id="trainer.professionalPath"
                  value={state.trainer.professionalPath}
                  name="trainer.professionalPath"
                  label={t('trainers.edit.professionalPath')}
                  labelClassName="col-form-label col-2"
                  disabled={false}
                  containerClassName="col-7"
                  placeholder={t('trainers.edit.professionalPath_placeholder')}
                  type="textarea"
                  groupProps={{ className: 'row' }}
                  onChange={value =>
                    handleChange('trainer.professionalPath', value)
                  }
                />
              </div>
            </div>
          )}
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

ExperiencesForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  organizationCompetenciesDomains: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      _id: PropTypes.string,
    }),
  ),
  onSkipStep: PropTypes.func,
};

ExperiencesForm.defaultProps = {
  organizationCompetenciesDomains: [],
  onSkipStep: null,
};

export default translate(ExperiencesForm);
